const NAMED_COLORS = new Set([
  'red', 'blue', 'green', 'black', 'white', 'gray', 'grey',
  'orange', 'yellow', 'purple', 'pink', 'brown', 'cyan', 'magenta',
  'lime', 'olive', 'navy', 'teal', 'maroon', 'aqua', 'fuchsia',
  'silver', 'tomato', 'coral', 'salmon', 'crimson', 'firebrick',
  'darkred', 'indianred', 'sienna', 'saddlebrown', 'chocolate',
  'peru', 'tan', 'wheat', 'bisque', 'beige', 'linen', 'ivory',
  'snow', 'seashell', 'lavender', 'thistle', 'plum', 'violet',
  'orchid', 'indigo', 'slateblue', 'darkslateblue', 'mediumpurple',
  'rebeccapurple', 'blueviolet', 'darkviolet', 'darkorchid',
  'darkmagenta', 'mediumvioletred', 'deeppink', 'hotpink',
  'palevioletred', 'lightpink', 'rosybrown', 'mistyrose',
  'gold', 'khaki', 'darkkhaki', 'goldenrod', 'darkgoldenrod',
  'lemonchiffon', 'lightyellow', 'papayawhip', 'moccasin',
  'peachpuff', 'palegoldenrod', 'cornsilk', 'oldlace',
  'floralwhite', 'honeydew', 'mintcream', 'azure', 'aliceblue',
  'ghostwhite', 'whitesmoke', 'gainsboro', 'lightgray', 'lightgrey',
  'darkgray', 'darkgrey', 'dimgray', 'dimgrey',
  'lightslategray', 'lightslategrey', 'slategray', 'slategrey',
  'darkslategray', 'darkslategrey',
  'lightcoral', 'darkorange', 'orangered', 'greenyellow',
  'chartreuse', 'lawngreen', 'limegreen', 'palegreen',
  'lightgreen', 'mediumspringgreen', 'springgreen', 'mediumseagreen',
  'seagreen', 'forestgreen', 'darkgreen', 'yellowgreen',
  'olivedrab', 'darkolivegreen', 'mediumaquamarine', 'darkseagreen',
  'lightseagreen', 'darkcyan', 'lightcyan', 'paleturquoise',
  'aquamarine', 'turquoise', 'mediumturquoise', 'darkturquoise',
  'cadetblue', 'steelblue', 'lightsteelblue', 'powderblue',
  'lightblue', 'skyblue', 'lightskyblue', 'deepskyblue',
  'dodgerblue', 'cornflowerblue', 'royalblue', 'mediumblue',
  'darkblue', 'midnightblue',
])

const ALLOWED_CHARS = /^[A-Za-z0-9@=#*>\-() ,./%\s\n\r]*$/
const INJECTION_PATTERNS = /url\(|expression\(|var\(|env\(|calc\(|import|javascript:|data:|\\|<|>|\{|\}|;/i
const TILE_NAME_RE = /^[A-Z][A-Z0-9]{0,7}$/
const HEX_COLOR_RE = /^#[0-9a-fA-F]{3,8}$/
const RGB_RE = /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*(0|1|0?\.\d+))?\s*\)$/
const HSL_RE = /^hsla?\(\s*\d{1,3}\s*,\s*\d{1,3}%\s*,\s*\d{1,3}%\s*(,\s*(0|1|0?\.\d+))?\s*\)$/
const SIZE_RE = /^\d+(\.\d+)?(px|em|rem)$/
const FILTER_FN_RE = /^(grayscale|sepia|brightness|contrast|saturate|blur|opacity|hue-rotate|invert)\(.+\)$/

const DIRECTIVES = new Set(['unit', 'height', 'gap', 'mortar', 'color', 'shadow', 'filter', 'jitter'])

const DEFAULTS = {
  unit: '30px',
  height: '20px',
  gap: '5px',
  mortar: '#B0B0B0',
  color: '#8B4513',
  shadow: 'inset 0 1px 0 0 rgba(255,255,255,0.31), inset 0 -1px 0 0 rgba(0,0,0,0.69), inset 1px 0 0 0 rgba(255,255,255,0.31), inset -1px 0 0 0 rgba(0,0,0,0.69)',
  filter: null,
  jitter: 0,
}

const MAX_DOC_LENGTH = 10240
const MAX_ROWS = 100
const MAX_TILES_PER_ROW = 100
const MAX_TILE_DEFS = 100
const MAX_DIRECTIVES = 20

function rejectInjection(val) {
  if (INJECTION_PATTERNS.test(val)) {
    throw new Error(`Rejected unsafe value: ${val.slice(0, 40)}`)
  }
}

function validateColor(val) {
  rejectInjection(val)
  const v = val.trim().toLowerCase()
  if (HEX_COLOR_RE.test(v)) return v
  if (NAMED_COLORS.has(v)) return v
  if (RGB_RE.test(v)) return v
  if (HSL_RE.test(v)) return v
  throw new Error(`Invalid color: ${val}`)
}

function validateSize(val) {
  rejectInjection(val)
  const v = val.trim()
  if (!SIZE_RE.test(v)) throw new Error(`Invalid size: ${val}`)
  return v
}

function validateFilter(val) {
  rejectInjection(val)
  if (val.trim().toLowerCase() === 'none') return null
  const parts = val.trim().split(/\s+/)
  for (const part of parts) {
    if (!FILTER_FN_RE.test(part)) throw new Error(`Invalid filter function: ${part}`)
  }
  return val.trim()
}

function validateShadow(val) {
  rejectInjection(val)
  if (val.trim().toLowerCase() === 'none') return null
  return val.trim()
}

function validateJitter(val) {
  const n = parseFloat(val)
  if (isNaN(n) || n < 0 || n > 10) throw new Error(`Invalid jitter: ${val}`)
  return n
}

// Tokenizer: classify each line
function tokenize(source) {
  if (source.length > MAX_DOC_LENGTH) {
    throw new Error(`Document exceeds max length of ${MAX_DOC_LENGTH} bytes`)
  }
  if (!ALLOWED_CHARS.test(source)) {
    throw new Error('Document contains disallowed characters')
  }

  const lines = source.split('\n')
  const tokens = []

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i]
    const trimmed = raw.trim()

    if (trimmed === '' || trimmed.startsWith('--')) {
      tokens.push({ type: 'skip', line: i })
      continue
    }

    if (trimmed.startsWith('@')) {
      const spaceIdx = trimmed.indexOf(' ')
      if (spaceIdx === -1) throw new Error(`Line ${i + 1}: directive missing value`)
      const key = trimmed.slice(1, spaceIdx).toLowerCase()
      const value = trimmed.slice(spaceIdx + 1).trim()
      tokens.push({ type: 'directive', key, value, line: i })
      continue
    }

    if (/^[A-Z][A-Z0-9]{0,7}\s*=/.test(trimmed)) {
      const eqIdx = trimmed.indexOf('=')
      const name = trimmed.slice(0, eqIdx).trim()
      const rest = trimmed.slice(eqIdx + 1).trim()
      const parts = rest.split(/\s+/)
      const width = parseInt(parts[0], 10)
      const color = parts.length > 1 ? parts.slice(1).join(' ') : null
      tokens.push({ type: 'tile_def', name, width, color, line: i })
      continue
    }

    tokens.push({ type: 'row', raw: trimmed, line: i })
  }

  return tokens
}

// Parser: build AST from tokens
function parse(tokens) {
  const ast = { directives: {}, tiles: {}, rows: [] }
  let directiveCount = 0, tileDefCount = 0

  for (const tok of tokens) {
    if (tok.type === 'skip') continue

    if (tok.type === 'directive') {
      if (!DIRECTIVES.has(tok.key)) throw new Error(`Line ${tok.line + 1}: unknown directive @${tok.key}`)
      if (++directiveCount > MAX_DIRECTIVES) throw new Error('Too many directives')
      ast.directives[tok.key] = tok.value
      continue
    }

    if (tok.type === 'tile_def') {
      if (!TILE_NAME_RE.test(tok.name)) throw new Error(`Line ${tok.line + 1}: invalid tile name "${tok.name}"`)
      if (tok.width < 1 || tok.width > 1000 || isNaN(tok.width)) throw new Error(`Line ${tok.line + 1}: invalid tile width`)
      if (++tileDefCount > MAX_TILE_DEFS) throw new Error('Too many tile definitions')
      ast.tiles[tok.name] = { width: tok.width, color: tok.color }
      continue
    }

    if (tok.type === 'row') {
      if (ast.rows.length >= MAX_ROWS) throw new Error('Too many rows')
      const row = parseRow(tok.raw, tok.line)
      ast.rows.push(row)
    }
  }

  if (ast.rows.length === 0) throw new Error('Pattern has no rows')
  return ast
}

function parseRow(raw, lineNum) {
  const parts = raw.split(/\s+/)
  const tiles = []
  let stagger = false
  let staggerOffset = null
  let repeat = 1

  for (let i = 0; i < parts.length; i++) {
    const p = parts[i]

    if (p.startsWith('>>')) {
      stagger = true
      const offsetStr = p.slice(2)
      if (offsetStr) {
        staggerOffset = parseInt(offsetStr, 10)
        if (isNaN(staggerOffset) || staggerOffset < 1 || staggerOffset > 1000) {
          throw new Error(`Line ${lineNum + 1}: invalid stagger offset`)
        }
      }
      continue
    }

    if (p.startsWith('*')) {
      repeat = parseInt(p.slice(1), 10)
      if (isNaN(repeat) || repeat < 1 || repeat > 100) {
        throw new Error(`Line ${lineNum + 1}: invalid repeat count`)
      }
      continue
    }

    if (/^\d+$/.test(p)) {
      tiles.push({ type: 'literal', width: parseInt(p, 10) })
    } else if (TILE_NAME_RE.test(p)) {
      tiles.push({ type: 'ref', name: p })
    } else {
      throw new Error(`Line ${lineNum + 1}: invalid token "${p}"`)
    }
  }

  if (tiles.length === 0) throw new Error(`Line ${lineNum + 1}: row has no tiles`)
  if (tiles.length > MAX_TILES_PER_ROW) throw new Error(`Line ${lineNum + 1}: too many tiles in row`)

  return { tiles, stagger, staggerOffset, repeat }
}

// Validator + Resolver
function validate(ast) {
  const d = { ...DEFAULTS }

  for (const [key, val] of Object.entries(ast.directives)) {
    rejectInjection(val)
    switch (key) {
      case 'unit':
      case 'height':
      case 'gap':
        d[key] = validateSize(val); break
      case 'mortar':
      case 'color':
        d[key] = validateColor(val); break
      case 'shadow':
        d[key] = validateShadow(val); break
      case 'filter':
        d[key] = validateFilter(val); break
      case 'jitter':
        d[key] = validateJitter(val); break
    }
  }

  for (const [, tile] of Object.entries(ast.tiles)) {
    if (tile.color) tile.color = validateColor(tile.color)
  }

  return d
}

function resolve(ast, directives) {
  const unitPx = parseFloat(directives.unit)

  const resolvedRows = []
  for (const row of ast.rows) {
    const resolvedTiles = row.tiles.map(t => {
      if (t.type === 'literal') {
        return { widthUnits: t.width, widthPx: t.width * unitPx, color: directives.color }
      }
      const def = ast.tiles[t.name]
      const widthUnits = def ? def.width : 2
      const color = (def && def.color) ? def.color : directives.color
      return { widthUnits, widthPx: widthUnits * unitPx, color }
    })

    for (let r = 0; r < row.repeat; r++) {
      resolvedRows.push({
        tiles: resolvedTiles,
        stagger: row.stagger,
        staggerOffset: row.staggerOffset,
      })
    }
  }

  return {
    directives,
    rows: resolvedRows,
    unitPx,
  }
}

// Emit: produce a wall config object compatible with the renderer
function emit(resolved) {
  const { directives, rows, unitPx } = resolved

  return {
    _bpl: true,
    rows,
    unitPx,

    stagger: (i) => {
      const row = rows[i % rows.length]
      return row.stagger
    },

    brick_h: directives.height,

    brick_w: ([i, j], row) => {
      const r = row || rows[i % rows.length]
      const tile = r.tiles[j % r.tiles.length]
      let w = tile.widthPx

      if (directives.jitter > 0) {
        const jitter = directives.jitter
        const seed = i * 1000 + j + 0.5
        const x = Math.sin(seed) * 10000
        const rand = x - Math.floor(x)
        const min = w / jitter
        const max = w * jitter
        w = min + rand * (max - min)
        w = Math.max(1, Math.round(w))
      }

      return w + 'px'
    },

    brick_color: ([i, j]) => {
      const r = rows[i % rows.length]
      return r.tiles[j % r.tiles.length].color
    },

    brick_gap: directives.gap,
    brick_gap_color: directives.mortar,

    "brick_[box-shadow]": directives.shadow,
    "brick_[filter]": directives.filter,

    stagger_offset: (i) => {
      const row = rows[i % rows.length]
      if (!row.stagger) return 0
      if (row.staggerOffset != null) return row.staggerOffset * unitPx
      const gapPx = parseFloat(directives.gap)
      // include gap after each tile: repeat = sum(tile_w + gap), half = repeat/2
      const totalWidth = row.tiles.reduce((sum, t) => sum + t.widthPx + gapPx, 0)
      return totalWidth / 2
    },
  }
}

export function compile(source) {
  const tokens = tokenize(source)
  const ast = parse(tokens)
  const directives = validate(ast)
  const resolved = resolve(ast, directives)
  return emit(resolved)
}

export function tryCompileFromBase64(encoded) {
  const source = atob(encoded)
  return compile(source)
}
